import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { abortDocumentReaderUpload } from '@commenda/api/commenda-ai/document-reader/abortDocumentReaderUpload';
import {
  DocumentReaderUploadStatus,
  type IDocumentReaderUploadLimits,
  type IDocumentReaderUploadReference,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';
import { toast } from '@commenda/kakashi';

import { DataRoomResumeRecordHelper } from './DataRoomResumeRecordHelper';
import { DataRoomUploadHelper } from './DataRoomUploadHelper';
import { DataRoomUploadTransfer } from './DataRoomUploadTransfer';
import {
  DataRoomFileState,
  type IDataRoomFileOutcome,
  type IDataRoomFileProgress,
  type IDataRoomLocalFile,
  type IDataRoomResumeRecord,
  type TDataRoomLimiter,
} from './types';

interface IUseDataRoomUploadParams {
  uploadLimits: IDocumentReaderUploadLimits | null;
  conversationId: string | null;
  companyId: number | null;
}

interface IDataRoomSession {
  uploadId: string;
  files: IDataRoomLocalFile[];
  completedFileIds: Set<string>;
  controller: AbortController;
  limitPart: TDataRoomLimiter;
}

const PROGRESS_FLUSH_MS = 200;

/** One data room per message: creates the upload, sends its parts in parallel with retries, resumes
 * after a reload from the parts S3 holds, and holds the per-file progress the composer draws. */
export const useDataRoomUpload = ({
  uploadLimits,
  conversationId,
  companyId,
}: Readonly<IUseDataRoomUploadParams>) => {
  const isAllowed = uploadLimits?.allowed === true && conversationId !== null;
  const storageKey = conversationId
    ? DataRoomResumeRecordHelper.resumeStorageKey(conversationId)
    : null;
  const [fileRows, setFileRows] = useState<IDataRoomFileProgress[]>([]);
  const [readyUpload, setReadyUpload] =
    useState<IDocumentReaderUploadReference | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStopped, setIsStopped] = useState(false);
  const [resumeRecord, setResumeRecord] =
    useState<IDataRoomResumeRecord | null>(null);
  const sessionRef = useRef<IDataRoomSession | null>(null);
  // Set as soon as a room starts, before its session exists, so cancel and unmount reach creation too.
  const controllerRef = useRef<AbortController | null>(null);
  const activeRunCountRef = useRef(0);
  const lastStatusRef = useRef<DocumentReaderUploadStatus | null>(null);
  const rowsRef = useRef(new Map<string, IDataRoomFileProgress>());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // sessionStorage is read in an effect, not during render, so the server render matches the first client one.
  useEffect(() => {
    if (storageKey)
      setResumeRecord(DataRoomResumeRecordHelper.readResumeRecord(storageKey));
    return () => {
      controllerRef.current?.abort();
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, [storageKey]);

  // Progress events arrive per chunk of every part in flight; drawing at most every 200 ms keeps a
  // 50,000-file room from re-rendering the composer on each one.
  const updateRow = useCallback(
    (
      fileId: string,
      patch: Partial<IDataRoomFileProgress>,
      isUrgent = false
    ) => {
      const row = rowsRef.current.get(fileId);
      if (row) rowsRef.current.set(fileId, { ...row, ...patch });
      if (flushTimerRef.current && !isUrgent) return;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(
        () => {
          flushTimerRef.current = null;
          setFileRows([...rowsRef.current.values()]);
        },
        isUrgent ? 0 : PROGRESS_FLUSH_MS
      );
    },
    []
  );

  const resetRoom = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    sessionRef.current = null;
    rowsRef.current = new Map();
    setFileRows([]);
    setReadyUpload(null);
    setIsUploading(false);
    setIsStopped(false);
    setResumeRecord(null);
    if (storageKey) DataRoomResumeRecordHelper.clearResumeRecord(storageKey);
  }, [storageKey]);

  const finishRuns = useCallback(async () => {
    const session = sessionRef.current;
    if (!session || activeRunCountRef.current > 0) return;
    setIsUploading(false);
    if (
      session.controller.signal.aborted ||
      session.files.some((file) => !session.completedFileIds.has(file.fileId))
    )
      return;
    const status =
      lastStatusRef.current === DocumentReaderUploadStatus.COMPLETED
        ? lastStatusRef.current
        : await DataRoomUploadTransfer.fetchUploadStatus(session.uploadId);
    if (sessionRef.current !== session) return;
    if (status !== DocumentReaderUploadStatus.COMPLETED) {
      // A resume has already named the files still missing; that message is the more useful one.
      setError(
        (current) =>
          current ??
          'Every file you chose is stored, but the data room is not complete yet. Choose the files still missing.'
      );
      return;
    }
    const rows = [...rowsRef.current.values()];
    setReadyUpload({
      uploadId: session.uploadId,
      fileCount: rows.length,
      totalSizeBytes: rows.reduce((sum, row) => sum + row.sizeBytes, 0),
    });
  }, []);

  const runFiles = useCallback(
    async (files: readonly IDataRoomLocalFile[]) => {
      const session = sessionRef.current;
      if (!session) return;
      const onFileSettled = (
        file: IDataRoomLocalFile,
        outcome: IDataRoomFileOutcome
      ) => {
        if (outcome.success) {
          session.completedFileIds.add(file.fileId);
          if (outcome.uploadStatus)
            lastStatusRef.current = outcome.uploadStatus;
          updateRow(
            file.fileId,
            {
              state: DataRoomFileState.STORED,
              sentBytes: file.selectedFile.file.size,
            },
            true
          );
          return;
        }
        updateRow(
          file.fileId,
          { state: DataRoomFileState.FAILED, error: outcome.error },
          true
        );
        if (outcome.isFatal && !session.controller.signal.aborted) {
          setError(outcome.error);
          setIsStopped(true);
          session.controller.abort();
        }
      };
      setIsUploading(true);
      files.forEach((file) =>
        updateRow(
          file.fileId,
          { state: DataRoomFileState.UPLOADING, error: null },
          true
        )
      );
      activeRunCountRef.current += 1;
      try {
        await DataRoomUploadTransfer.uploadFiles(
          {
            uploadId: session.uploadId,
            maxPartUrlsPerRequest: uploadLimits?.maxPartUrlsPerRequest ?? 100,
            signal: session.controller.signal,
            limitPart: session.limitPart,
            onFileBytes: (fileId, sentBytes) =>
              updateRow(fileId, { sentBytes }),
          },
          files,
          onFileSettled
        );
      } finally {
        activeRunCountRef.current -= 1;
      }
      if (sessionRef.current !== session) return;
      // A stopped room never starts its remaining files, so they show as failed instead of moving forever.
      if (session.controller.signal.aborted)
        files.forEach((file) => {
          if (
            rowsRef.current.get(file.fileId)?.state ===
            DataRoomFileState.UPLOADING
          )
            updateRow(file.fileId, { state: DataRoomFileState.FAILED }, true);
        });
      await finishRuns();
    },
    [uploadLimits, updateRow, finishRuns]
  );

  const beginSession = useCallback(
    (
      uploadId: string,
      files: IDataRoomLocalFile[],
      storedRows: readonly IDataRoomFileProgress[],
      controller: AbortController
    ) => {
      const session: IDataRoomSession = {
        uploadId,
        files,
        completedFileIds: new Set(),
        controller,
        limitPart: DataRoomUploadTransfer.createLimiter(
          DataRoomUploadHelper.PARTS_IN_FLIGHT
        ),
      };
      sessionRef.current = session;
      setIsStopped(false);
      activeRunCountRef.current = 0;
      lastStatusRef.current = null;
      rowsRef.current = new Map(
        [...files.map(DataRoomUploadHelper.toFileProgress), ...storedRows].map(
          (row) => [row.fileId, row]
        )
      );
      setFileRows([...rowsRef.current.values()]);
      runFiles(files).catch(() =>
        setError('The data room upload stopped unexpectedly.')
      );
    },
    [runFiles]
  );

  const resumeUpload = useCallback(
    async (
      record: IDataRoomResumeRecord,
      uploads: readonly CommendaAiUploadFile[],
      controller: AbortController
    ) => {
      const loaded = await DataRoomUploadTransfer.loadResume(
        record,
        uploads,
        controller.signal
      );
      if (controller.signal.aborted) return;
      if (loaded.success === false) {
        setIsUploading(false);
        setError(loaded.error);
        if (loaded.status === DocumentReaderUploadStatus.ABORTED) resetRoom();
        return;
      }
      const resumeError = DataRoomUploadHelper.getResumeError(
        loaded.unmatchedNames,
        loaded.abortedNames
      );
      if (resumeError) setError(resumeError);
      // Seeded from the parts S3 holds, so only each file's missing parts are sent again.
      const missingPartCount = loaded.files.reduce(
        (sum, file) =>
          sum + DataRoomUploadHelper.missingPartNumbers(file).length,
        0
      );
      toast.info(
        `Resuming the data room: ${missingPartCount} parts left to send.`
      );
      beginSession(
        record.uploadId,
        loaded.files,
        loaded.storedRows,
        controller
      );
    },
    [beginSession, resetRoom]
  );

  const startUpload = useCallback(
    async (selected: readonly CommendaAiUploadFile[]) => {
      if (!isAllowed || !uploadLimits || !conversationId || !storageKey) return;
      // A resumed room that still misses files takes another selection; it is re-read from the server.
      const canReselect = !!resumeRecord && !isUploading && !readyUpload;
      if ((sessionRef.current && !canReselect) || readyUpload || isUploading) {
        toast.error(
          'A message takes one data room. Send or cancel the current one first.'
        );
        return;
      }
      const { uploads, emptyNames } = DataRoomUploadHelper.splitEmpty(selected);
      if (emptyNames.length > 0)
        toast.warning(
          `Skipped empty files: ${DataRoomUploadHelper.formatNameList(emptyNames)}`
        );
      const limitError = DataRoomUploadHelper.getLimitError(
        uploads,
        uploadLimits
      );
      setError(limitError);
      if (limitError) return;

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      if (resumeRecord) {
        sessionRef.current = null;
        setIsUploading(true);
        await resumeUpload(resumeRecord, uploads, controller);
        return;
      }
      setIsUploading(true);
      const created = await DataRoomUploadTransfer.createRoom({
        uploads,
        limits: uploadLimits,
        conversationId,
        companyId,
        signal: controller.signal,
        onRecordChange: (record) =>
          DataRoomResumeRecordHelper.saveResumeRecord(storageKey, record),
      });
      if (controller.signal.aborted) {
        // Cancelled or left while the room was being created, so nothing uploads in the background.
        if (created.success)
          abortDocumentReaderUpload(created.uploadId).catch(() => undefined);
        DataRoomResumeRecordHelper.clearResumeRecord(storageKey);
        return;
      }
      if (created.success === false) {
        setIsUploading(false);
        setError(created.error);
        DataRoomResumeRecordHelper.clearResumeRecord(storageKey);
        return;
      }
      if (created.skippedEmptyFileNames.length > 0)
        toast.warning(
          `Skipped empty files: ${DataRoomUploadHelper.formatNameList(created.skippedEmptyFileNames)}`
        );
      beginSession(created.uploadId, created.files, [], controller);
    },
    [
      isAllowed,
      uploadLimits,
      conversationId,
      companyId,
      storageKey,
      readyUpload,
      resumeRecord,
      isUploading,
      resumeUpload,
      beginSession,
    ]
  );

  /** Re-runs one failed file; only the parts S3 does not hold are sent again. */
  const retryFile = useCallback(
    (fileId: string) => {
      const session = sessionRef.current;
      const file = session?.files.find(
        (candidate) => candidate.fileId === fileId
      );
      if (!session || !file || session.controller.signal.aborted) return;
      setError(null);
      runFiles([file]).catch(() =>
        setError('The data room upload stopped unexpectedly.')
      );
    },
    [runFiles]
  );

  /** Aborts an unfinished upload on the server too; a stored room is only dropped from this message. */
  const cancelUpload = useCallback(() => {
    const uploadId = readyUpload
      ? null
      : (sessionRef.current?.uploadId ?? resumeRecord?.uploadId ?? null);
    resetRoom();
    setError(null);
    if (uploadId)
      abortDocumentReaderUpload(uploadId)
        .then((aborted) => {
          if (!aborted.success)
            toast.error(
              aborted.error ?? 'Could not cancel the data room upload.'
            );
        })
        .catch(() => undefined);
  }, [readyUpload, resumeRecord, resetRoom]);

  const progress = useMemo(
    () =>
      fileRows.length > 0 ? DataRoomUploadHelper.summarise(fileRows) : null,
    [fileRows]
  );

  return {
    isAllowed,
    progress,
    readyUpload,
    isUploading,
    isPreparing: isUploading && fileRows.length === 0,
    error,
    resumeFileCount:
      isAllowed && !isUploading && fileRows.length === 0
        ? (resumeRecord?.fileCount ?? null)
        : null,
    startUpload,
    retryFile: isStopped ? null : retryFile,
    cancelUpload,
    clearAfterSend: resetRoom,
  };
};
